import { useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { ChannelToggle, type Channel } from '../components/ChannelToggle'
import { VideoGuidePanel, type VideoGuide } from '../components/VideoGuidePanel'

const STEPS = [
  {
    step: 1,
    title: 'Review the Preview',
    items: [
      <>Click the link in your email notification to open the preview section</>,
      <>Click the Preview Link to open the ad preview in your browser</>,
      <>Review: <strong className="text-slate-800">Copy</strong> (accurate, localised, on-brand), <strong className="text-slate-800">Asset</strong> (matches expected), <strong className="text-slate-800">Format</strong> (renders correctly), <strong className="text-slate-800">Legal elements</strong> (disclaimers present)</>,
    ],
    note: null,
  },
  {
    step: 2,
    title: 'Provide Feedback (if changes are needed)',
    items: [
      <>Open the Status Overlay on the row</>,
      <>Go to the Preview Comments section</>,
      <>Add your feedback clearly and specifically</>,
    ],
    note: 'The Activation Team will be notified immediately. The campaign will not be published until you approve.',
  },
  {
    step: 3,
    title: 'Approve the Preview',
    items: [
      <>If the preview meets all requirements, click <strong className="text-slate-800">Approve</strong></>,
      <>The Activation Team is notified and is now authorised to publish the campaign</>,
    ],
    note: null,
  },
]

function StepList({ steps, color }: { steps: { step: number; title: string; items: React.ReactNode[]; note?: string | null }[]; color: string }) {
  return (
    <div className="space-y-0">
      {steps.map(({ step, title, items, note }, i, arr) => (
        <div key={step} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className={`w-7 h-7 rounded-full bg-${color}-100 text-${color}-700 text-xs font-bold flex items-center justify-center shrink-0 z-10`}>
              {step}
            </div>
            {i < arr.length - 1 && (
              <div className="w-px flex-1 bg-slate-200 my-1.5" />
            )}
          </div>
          <div className={`min-w-0 flex-1 ${i < arr.length - 1 ? 'pb-6' : ''}`}>
            <p className="text-sm font-semibold text-slate-800 mt-0.5 mb-2">{title}</p>
            <ul className="space-y-1.5">
              {items.map((item, j) => (
                <li key={j} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                  <span className={`text-${color}-300 shrink-0 mt-0.5`}>•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {note && (
              <div className={`mt-3 border-l-[3px] border-${color}-300 pl-3 py-0.5`}>
                <p className={`text-sm text-${color}-800 leading-relaxed`}>{note}</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

const VIDEOS: VideoGuide[] = [
  { title: 'Approver Process', description: 'Review and approval walkthrough', src: '/api/static/content-tracker/about-videos/Approver Process .mp4', channel: 'both' },
  { title: 'Adding Assets in Content Modal', description: 'Images & ad copy walkthrough', src: '/api/static/content-tracker/about-videos/How to add assets in the Content Modal (images & ad copy).mp4', channel: 'both' },
]

export function ApproverTab() {
  const [channel, setChannel] = useState<Channel>('social');

  return (
    <div className="flex gap-8 max-w-6xl mx-auto">
      <div className="flex-1 min-w-0 space-y-6">
        <ChannelToggle value={channel} onChange={setChannel} color="orange" />

        {/* Intro */}
        <Card className="shadow-none border-slate-300">
          <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Approver</p>
          </div>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-orange-700" strokeWidth={1.75} />
              </div>
              <CardTitle className="text-xl">Approver (Client) Guidelines</CardTitle>
            </div>
            <CardDescription className="leading-relaxed mt-1">
              Review campaign previews and confirm approval before campaigns go live.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-l-[3px] border-orange-400 pl-4 py-0.5">
              <p className="text-sm text-slate-700 leading-relaxed">
                Your job is to review campaign previews and confirm they meet Ford's standards before they are published.
              </p>
            </div>
            <div className="border-l-[3px] border-orange-300 pl-4 py-0.5">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-orange-700 mb-1">Starts when</p>
              <p className="text-sm text-slate-700 leading-relaxed">
                You will receive an <strong className="text-slate-800">email notification</strong> when a preview is ready for your review. The notification includes a direct link to the preview section.
              </p>
            </div>
            <div className="border-l-[3px] border-orange-300 pl-4 py-0.5">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-orange-700 mb-1">Client ad copy</p>
              <p className="text-sm text-slate-700 leading-relaxed">
                For the ESO markets, clients are adding copy directly into the tool. You will also get a notification when this needs to be done via email.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Process */}
        <Card className="shadow-none border-slate-300">
          <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Process</p>
          </div>
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">Your 3-step process</CardTitle>
          </CardHeader>
          <CardContent>
            <StepList steps={STEPS} color="orange" />
          </CardContent>
        </Card>

        {/* Special Case */}
        <Card className="shadow-none border-slate-300">
          <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Special case</p>
          </div>
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">Markets where Approver also creates ad copy</CardTitle>
            <CardDescription className="leading-relaxed mt-1">
              In some markets, the Approver is also responsible for completing the Ad Copy Creation step.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="border-l-[3px] border-orange-300 pl-4 py-1 space-y-2">
              <p className="text-sm text-slate-700 leading-relaxed">If this applies to your market:</p>
              <ul className="space-y-1.5">
                {[
                  <>You will receive an email notification when the status reaches <strong className="text-slate-800">Ad Copy Creation</strong></>,
                  'Follow the Creative Agency process to complete ad copy and approve the bundle',
                  'Then follow the Approver process to review and approve the preview',
                ].map((item, i) => (
                  <li key={i} className="flex gap-2 text-sm text-slate-500 leading-relaxed">
                    <span className="text-orange-300 shrink-0 mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm font-semibold text-slate-700">Both processes must be completed in order. Do not approve the preview before completing ad copy validation.</p>
            </div>
          </CardContent>
        </Card>

        {/* Live Updates — same for both channels */}
        <Card className="shadow-none border-slate-300">
          <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Live updates</p>
          </div>
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">Brief update to live assets</CardTitle>
            <CardDescription className="leading-relaxed mt-1">
              If an update is required for an existing live campaign, there are two paths you can take.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-orange-700 mb-4">Path 1: Copy changes, URL change, or pausing assets</p>
              <StepList steps={[
                { step: 1, title: 'Make Your Updates', items: [<>Locate the relevant campaign in Content Tracker and open the Asset Overlay</>, <>Make your updates (copy changes, URL changes)</>, <>Click <strong className="text-slate-800">Save & Validate</strong></>, <>Clear all errors and warnings</>] },
                { step: 2, title: 'Reference Your Changes', items: [<>Reference your changes in the <strong className="text-slate-800">Asset Feedback</strong> section</>, <>You can also advise if assets need to be paused</>, <>Tag your Mindshare planner in the Asset Feedback section</>] },
                { step: 3, title: 'Approve Updated Preview', items: [<>Once the updates have been implemented in platform, you will receive a notification that a preview link is ready for approval</>, <>Approve the preview link, and Activation will set the campaign live</>] },
              ]} color="orange" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-orange-700 mb-4">Path 2: Creative update (updating imagery or videos)</p>
              <StepList steps={[
                { step: 1, title: 'Brief the Team', items: [<>Locate the relevant campaign in Content Tracker that needs a creative update</>, <>Tag your Mindshare Planner and the Creative Agency in the <strong className="text-slate-800">Asset Feedback</strong> section, briefing them on the required update</>] },
                { step: 2, title: 'Approve Updated Preview', items: [<>Once the updates have been implemented in platform, you will receive a notification that a preview link is ready for approval</>, <>Approve the preview link, and Activation will set the campaign live</>] },
              ]} color="orange" />
            </div>
          </CardContent>
        </Card>

        {/* Meta Campaign Codes — Social only */}
        {channel === 'social' && (
          <Card className="shadow-none border-slate-300">
            <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Meta</p>
            </div>
            <CardHeader className="pb-4">
              <CardTitle className="text-xl">Meta campaign codes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border-l-[3px] border-orange-300 pl-4 py-0.5">
                <p className="text-sm text-slate-700 leading-relaxed">
                  If you have a Meta Campaign code for your delivery, go to the <strong className="text-slate-800">Asset Overlay</strong> and scroll down to the <strong className="text-slate-800">Asset Feedback</strong> section. Insert your code as a comment and tag your planner.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="w-64 flex-shrink-0 sticky top-20 self-start">
        <VideoGuidePanel videos={VIDEOS} />
      </div>
    </div>
  )
}
